<script setup lang="ts">
import { ref, onMounted } from "vue";
import SearchSelect from "../../../../components/ui/form/SearchSelect.vue";
import { useFormValidation } from "../../../../composables/useFormValidation";
import { api } from "../../../../api/Api";
import Toast from "../../../../services/Toast";
import { Permission } from "../../../../types/auth";
import { required } from "../../../../utils/validation";
import { Item } from "../../../../types/models";
import { formatDate, isEmpty } from "../../../../utils/CommonFunctions";
import MessageBox from "../../../../services/MessageBox";
import { useScreenSize } from "../../../../utils/useScreenSize";
import ModalFormComponent from "../../../../components/ui/common/ModalFormComponent.vue";
import DocumentHeader from "../../../../components/ui/Templates/DocumentHeader.vue";
import IconComponent from "../../../../components/ui/common/IconComponent.vue";
import TabComponent from "../../../../components/ui/form/TabComponent.vue";
import AccountingLog from "../../../../components/ui/Templates/AccountingLog.vue";
import DocumentFooter from "../../../../components/ui/Templates/DocumentFooter.vue";
import ButtonInput from "../../../../components/ui/form/ButtonInput.vue";
import DataTable from "../../../../components/table/DataTable.vue";
import { TableField } from "../../../../components/table/table";
import Buttons from "../../../../components/ui/form/Buttons.vue";
import ListSelectModalInput from "../../../../components/ui/form/ListSelectModalInput.vue";
import ModalFormTemplate from "../../../../components/ui/Templates/ModalFormTemplate.vue";
import TextInput from "../../../../components/ui/form/TextInput.vue";
import NumberInput from "../../../../components/ui/form/NumberInput.vue";

interface CostingSheetBills {
  uid: string;
  vendor: string;
  costing_sheets_id: number | null;
  purchase_invoices_id: number | null;
  document_no: string;
  general_ledgers_id: number | null;
  ledger_group_id: any
  currencys_id: number;
  exchange_rate: number;
  amount: number;
  base_amount: number;
  absorbment: number;
  remarks: string;
  items: CostingSheetBillItems[];
  status_id: number;
  users_id: number;
}

interface CostingSheetBillItems {
  id: number | string | null;
  costing_sheets_id: number | null;
  costing_sheet_bills_id: number;
  code: string;
  name: string;
  items_id: number | string;
  item_variants_id: number | string | null;
  purchase_invoice_items_id: number;
  amount: number;
  ratio: number;
  quantity: number;
  value_before_tax: number;
  remarks: string;
  users_id?: number;
  status_id?: number;
}

interface InvoiceItem {
  id: number;
  items_id: number | string;
  photo?: string;
  purchase_invoices_id: number | string | null;
  purchase_invoice_items_id: number;
  code: string;
  name: string;
  variant?: string;
  quantity: number;
  inventory_qty: number;
  on_hand: number;
  cost: number;
  net_total: number;
  inventory_cost: number;
  additional_amount: number;
  price: number;
  remarks: string;
  tracked_by: string;
  item_variants_id: number | string | null;
  sub_total: number;
  discount_p: number;
  discount_a: number;
  value_before_tax: number;
  amount_ratio: number;
  quantity_ratio: number;
  equal_ratio: number;
  tax_amount: number;
  current_inventory_cost: number;
  current_unit_cost: number;
  new_unit_cost: number;
  uid: string;
}

interface Ledger {
  amount: number;
  debit: number;
  credit: number;
  transaction_type: string;
  chart_of_account: string;
  chart_of_accounts_id: number;
  currency: string;
  currencys_id: number;
  date: string;
  exchange_rate: number;
  id: number;
  ledger_group_id: number;
  record_id: number;
  record_type: string;
  reference: string;
  uid: string;
}

const invoiceItems = ref<InvoiceItem[]>([]);
const bills = ref<CostingSheetBills[]>([]);
const expenses = ref<CostingSheetBills[]>([]);
const ledgers = ref<Ledger[]>([]);

const props = defineProps<{
  data?: any | null;
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus";
  title: string | null;
  endPoint: string;
  permissions?: Permission[];
  listCreateName?: string;
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "save"): void;
  (e: "viewDocument", data: any, routePath: string, title: string): void;
}>();

const { isMobile } = useScreenSize();
const isReadOnly = ref<boolean>(props.mode === "view");

const {
  fields,
  formData,
  setFieldRules,
  validateForm,
  setFormData,
  resetForm,
} = useFormValidation({
  purchase_invoices_id: props.data?.purchase_invoices_id || "",
  purchase_document_no: props.data?.purchase_document_no || "",
  date_time: props.data?.date_time || new Date().toISOString().split("T")[0],
  document_no: props.data?.document_no || "",
  prefix: props.data?.prefix || "",
  suffix: props.data?.suffix || "",
  ref_no: props.data?.ref_no || "",
  status_id: props.data?.status_id || "",
  uid: props.data?.uid || "",
  items: props.data?.items || ([] as Item[]),
  bills: props.data?.bills || [],
  expenses: props.data?.expenses || [],
  ledgers: props.data?.ledgers || [],
  remarks: props.data?.remarks || "",
  attachment: props.data?.attachment || [],
  action: "",
  accounting: [],
  invoice: null as any,
});

setFieldRules("purchase_document_no", [required("Select Purchase Invoice")]);
setFieldRules("items", [required("Select Warehouse")]);

const setData = (data: any) => {
  setFormData(data);
  fields.value.date_time.value = formatDate(data.date_time);
  fields.value.bills.value = data.bills;
  fields.value.expenses.value = data.expenses;
  fields.value.accounting.value = data.accounting;
  invoiceItems.value = data.items;
  bills.value = data.bills;
  expenses.value = data.expenses;
  isReadOnly.value = true;
};

const getRecord = async () => {
  if (isEmpty(props.data)) return;
  const params = {
    id: props.data.uid,
  };
  const response = await api.post(
    (props.endPoint.slice(-1) == "/" ? props.endPoint : props.endPoint + "/") +
      "record",
    params
  );
  if (response.success == true) {
    setData(response.data);
  }
};

const handleSubmit = async () => {
  fields.value.items.value = invoiceItems.value;
  fields.value.bills.value = bills.value;
  fields.value.expenses.value = expenses.value;
  fields.value.ledgers.value = ledgers.value;
  if (validateForm()) {
    if (invoiceItems.value.length <= 0) {
      MessageBox.show("Select at least 1 item", "warning");
      return;
    }
    if (bills.value.length <= 0 && expenses.value.length <= 0) {
      MessageBox.show("Select at least 1 Bill or Expense to absorb", "warning");
      return;
    }
    if (ledgers.value.length <= 0) {
      MessageBox.show("Ledger details missing", "warning");
      return;
    }
    const response = await api.post(
      (props.endPoint.slice(-1) == "/"
        ? props.endPoint
        : props.endPoint + "/") + "create",
      formData.value
    );
    if (response.success == true) {
      isReadOnly.value = true;
      Toast.show(props.title + " Saved successfully");
      emit("save");
      setData(response.data);
    }
  }
};

const clearForm = () => {
  resetForm();
  fields.value.date_time.value = formatDate(new Date());
};

const handleInvoiceSelect = async (data: any) => {
  const response = await api.post(
    (props.endPoint.slice(-1) == "/" ? props.endPoint : props.endPoint + "/") +
      "get-items",
    {
      id: data.uid,
    }
  );
  if (response.success == true) {
    fields.value.purchase_invoices_id.value = data.id;
    invoiceItems.value = response.data;
    const totalAmount = invoiceItems.value.reduce((sum: number, item: any) => {
      return sum + item.cost * item.quantity;
    }, 0);
    const totalQty = invoiceItems.value.reduce((sum: number, item: any) => {
      return sum + item.quantity;
    }, 0);
    invoiceItems.value.forEach((item) => {
      item.additional_amount = 0;
      item.new_unit_cost = 0;
      item.current_inventory_cost = item.inventory_cost * item.on_hand;
      item.current_unit_cost = item.inventory_cost;
      item.new_unit_cost = item.cost;
      item.amount_ratio = (item.cost * item.quantity) / totalAmount;
      item.quantity_ratio = item.quantity / totalQty;
      item.equal_ratio = 1 / invoiceItems.value.length;
    });
  }
};

const activeTab = ref("items");

const viewDocument = () => {
  emit(
    "viewDocument",
    formData.value.invoice,
    "Purchase Invoice",
    "procurement/purchases/purchase-invoices"
  );
};

const showForm = ref(false);
onMounted(() => {
  showForm.value = true;
  isReadOnly.value = props.mode == "view";
  if (props.mode != "create") {
    getRecord();
  } else {
    clearForm();
  }
});

const itemTableFields: TableField[] = [
  { name: "code", caption: "Code", sortable: false, fieldType: "text" },
  { name: "name", caption: "Item Name", sortable: false, fieldType: "text" },
  {
    name: "quantity",
    caption: "Purchase Quantity",
    sortable: false,
    fieldType: "number",
    width: "150px",
  },
  {
    name: "on_hand",
    caption: "On Hand Quantity",
    sortable: false,
    fieldType: "number",
    width: "150px",
  },
  {
    name: "cost",
    caption: "Purchase Price",
    sortable: false,
    fieldType: "number",
    width: "150px",
  },
  {
    name: "current_inventory_cost",
    caption: "Inventory Value",
    sortable: false,
    fieldType: "number",
    width: "150px",
  },
  {
    name: "inventory_cost",
    caption: "Current Cost",
    sortable: false,
    fieldType: "number",
    width: "150px",
  },
  {
    name: "additional_amount",
    caption: "Add. Amount",
    sortable: false,
    fieldType: "number",
    width: "150px",
  },
  {
    name: "new_unit_cost",
    caption: "New Cost",
    sortable: false,
    fieldType: "number",
    width: "150px",
  },
  {
    name: "new_inventory_unit_cost",
    caption: "New Inventory Cost",
    sortable: false,
    fieldType: "number",
    width: "150px",
  },
  {
    name: "price",
    caption: "Price",
    sortable: false,
    fieldType: "number",
    width: "150px",
  },
  { name: "remarks", caption: "Remarks", sortable: false, fieldType: "text" },
];

const showBillsForm = ref(false);
const showBillAbsorbForm = ref(false);
const selectedBill = ref<CostingSheetBills>();

const handleAddNewBill = () => {
  showBillsForm.value = true;
};
const handleAddBills = (data: any) => {
  selectedBill.value = {
    uid: data.uid,
    vendor: data.vendor,
    costing_sheets_id: data.costing_sheets_id,
    purchase_invoices_id: data.id,
    document_no: data.document_no,
    general_ledgers_id: null,
    currencys_id: data.currencys_id,
    ledger_group_id: data.ledger_group_id,
    exchange_rate: data.exchange_rate,
    amount: data.value_before_tax,
    base_amount: data.value_before_tax * data.exchange_rate,
    absorbment: data.absorbment || "",
    remarks: data.remarks,
    items: [],
    status_id: data.status_id,
    users_id: data.users_id,
  };
  invoiceItems.value.forEach((item) => {
    selectedBill.value?.items.push({
      id: null,
      costing_sheets_id: null,
      costing_sheet_bills_id: data.id,
      items_id: item.items_id,
      item_variants_id: item.item_variants_id,
      amount: 0,
      ratio: 0,
      remarks: item.remarks,
      code: item.code,
      name: item.name,
      quantity: item.quantity,
      value_before_tax: item.value_before_tax,
      purchase_invoice_items_id: item.id,
    });
  });
  showBillAbsorbForm.value = true;
  showBillsForm.value = false;
};

const calculateNewCost = () => {
  invoiceItems.value.forEach((item: any) => {
    let allocatedAmount = 0;
    bills.value.forEach((bill: any) => {
      if (bill.items) {
        const bill_item = bill.items.find(
          (i: any) => i.items_id == item.items_id
        );
        console.log(bill_item);
        if (bill_item) {
          allocatedAmount += bill_item.amount / bill_item.quantity;
          console.log("allocatedAmount", allocatedAmount);
        }
      }
    });
    expenses.value.forEach((expense: any) => {
      if (expense.items) {
        const expense_item = expense.items.find(
          (i: any) => i.items_id == item.items_id
        );
        if (expense_item) {
          allocatedAmount += expense_item.amount / expense_item.quantity;
        }
      }
    });
    item.additional_amount = allocatedAmount;
    item.new_unit_cost = item.cost + item.additional_amount;
    let cost_val = item.new_unit_cost * item.quantity;
    let inv_val = item.on_hand * item.inventory_cost;
    item.new_inventory_unit_cost =
      (cost_val + inv_val) / (item.on_hand + item.quantity);
  });
};
const handleAbsorbmentChanged = (data: any) => {
  if (data) {
    selectedBill.value?.items.forEach((item: any) => {
      const inv_item = invoiceItems.value.find(
        (i: any) => i.items_id == item.items_id
      );
      console.log(inv_item);
      if (inv_item) {
        const amount = selectedBill.value?.amount ?? 0;
        if (data.id == "quantity") {
          item.ratio = inv_item?.quantity_ratio;
          item.amount = amount * inv_item?.quantity_ratio;
        } else if (data.id == "amount") {
          item.ratio = inv_item?.amount_ratio;
          item.amount = amount * inv_item?.amount_ratio;
        } else if (data.id == "equally") {
          item.ratio = inv_item?.equal_ratio;
          item.amount = amount * inv_item?.equal_ratio;
        }
      }
    });
  } else {
    selectedBill.value?.items.forEach((item: any) => {
      item.amount = 0;
    });
  }
};

const handleUpdateBills = async () => {
  if (
    !selectedBill.value ||
    !selectedBill.value.absorbment ||
    !selectedBill.value.purchase_invoices_id ||
    !selectedBill.value.items ||
    selectedBill.value.items?.length <= 0
  )
    return;
  bills.value.push(selectedBill.value);
  calculateNewCost();
  handleGetLedgers(
    "PurchaseInvoices",
    selectedBill.value?.purchase_invoices_id?.toString(),
    selectedBill.value?.amount ?? 0,
    selectedBill.value?.uid,
    selectedBill.value?.ledger_group_id || ""
  );
  showBillAbsorbForm.value = false;
};

const handleGetLedgers = async (
  record_type: string,
  record_id: string,
  amount: number,
  ledger_id: string = "",
  ledger_group_id: string = ""
) => {
  const response = await api.post(
    "finance/accounting/ledger-entries/get-ledger",
    {
      type: "dr",
      record_type: record_type,
      record_id: record_id,
      amount: amount,
      id: ledger_id,
      ledger_group_id: ledger_group_id
    }
  );
  if (response.success == true) {
    if (response.data.length >= 1) {
      //const ledger = response.data[0];
      response.data.forEach((ledger: any) => {
        ledgers.value.push({
          amount: ledger.amount,
          chart_of_account: ledger.chart_of_account,
          chart_of_accounts_id: ledger.chart_of_accounts_id,
          currency: ledger.currency,
          date: ledger.date,
          currencys_id: ledger.currencys_id,
          exchange_rate: ledger.exchange_rate,
          id: ledger.id,
          ledger_group_id: ledger.ledger_group_id,
          record_id: ledger.record_id,
          record_type: ledger.record_type,
          reference: record_type
            ? record_type + ": " + record_id + "/" + ledger.id
            : "ExpenseLedger: " + ledger.id,
          uid: ledger.uid,
          transaction_type: ledger.transaction_type == "dr" ? "cr" : "dr",
          debit: ledger.transaction_type == "dr" ? 0 : ledger.amount,
          credit: ledger.transaction_type == "cr" ? 0 : ledger.amount,
        });
        ledgers.value.push({
          amount: ledger.amount,
          chart_of_account: "Inventory Account",
          chart_of_accounts_id: 0,
          currency: ledger.currency,
          currencys_id: ledger.currencys_id,
          date: ledger.date,
          exchange_rate: ledger.exchange_rate,
          id: ledger.id,
          ledger_group_id: ledger.ledger_group_id,
          record_id: ledger.record_id,
          record_type: ledger.record_type,
          reference: record_type
            ? record_type + ": " + record_id + "/" + ledger.id
            : "ExpenseLedger: " + ledger.id,
          uid: ledger.uid,
          transaction_type: ledger.transaction_type == "cr" ? "cr" : "dr",
          debit: ledger.transaction_type == "cr" ? 0 : ledger.amount,
          credit: ledger.transaction_type == "dr" ? 0 : ledger.amount,
        });
      });
    } else if (response.data.length > 1) {
      MessageBox.show("Multiple ledgers found, please select one", "warning");
    } else {
      MessageBox.show("No ledgers found", "warning");
    }
  }
};

const showExpenseForm = ref(false);
const showExpenseAbsorbForm = ref(false);
const handleAddNewExpense = () => {
  showExpenseForm.value = true;
};

const handleAddExpense = (data: any) => {
  selectedBill.value = {
    uid: data.uid,
    vendor: data.vendor,
    costing_sheets_id: data.costing_sheets_id,
    purchase_invoices_id: null,
    document_no: data.chart_of_account,
    general_ledgers_id: data.id,
    currencys_id: data.currencys_id,
    exchange_rate: data.exchange_rate,
    amount: data.amount,
    base_amount: data.amount * data.exchange_rate,
    ledger_group_id: data.ledger_group_id,
    absorbment: data.absorbment || "",
    remarks: data.remarks,
    items: [],
    status_id: data.status_id,
    users_id: data.users_id,
  };
  invoiceItems.value.forEach((item) => {
    selectedBill.value?.items.push({
      id: null,
      costing_sheets_id: null,
      costing_sheet_bills_id: data.id,
      items_id: item.items_id,
      item_variants_id: item.item_variants_id,
      amount: 0,
      ratio: 0,
      remarks: item.remarks,
      code: item.code,
      name: item.name,
      quantity: item.quantity,
      value_before_tax: item.value_before_tax,
      purchase_invoice_items_id: item.id,
    });
  });
  showExpenseAbsorbForm.value = true;
  showExpenseForm.value = false;
};

const handleUpdateExpenses = async () => {
  if (
    !selectedBill.value ||
    !selectedBill.value.absorbment ||
    !selectedBill.value.general_ledgers_id ||
    !selectedBill.value.items ||
    selectedBill.value.items?.length <= 0
  )
    return;

  expenses.value.push(selectedBill.value);
  calculateNewCost();
  handleGetLedgers(
    "",
    "",
    selectedBill.value?.amount ?? 0,
    selectedBill.value?.uid,
    selectedBill.value?.ledger_group_id || ""
  );
  showExpenseAbsorbForm.value = false;
};
</script>

<template>
  <ModalFormComponent width="100%" @close="$emit('close')">
    <template #header>
      <!-- Header -->
      <DocumentHeader
        :title="title"
        :end-point="endPoint"
        :data="formData"
        :readonly="isReadOnly"
        :show-drop-down="false"
        @close="$emit('close')"
        :permissions="permissions"
        @clear="clearForm"
        @save="handleSubmit"
        @new="clearForm"
        :show-share-icons="false"
      >
        <template #dropdownbuttons>
          <button
            @click="viewDocument"
            v-if="formData.invoice && formData.invoice?.document_no"
            class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <IconComponent name="FileInput" />
            View Purchase Invoice ({{ formData.invoice?.document_no }})
          </button>
        </template>
      </DocumentHeader>
    </template>

    <!-- Main Content - Scrollable -->
    <template #form>
      <div class="grid md:grid-cols-6 gap-6 mb-8">
        <div class="flex justify-between items-center mb-2">
          <ButtonInput
            label="Purchase Invoice"
            end-point="procurement/purchases/purchase-invoices"
            v-model="fields.purchase_document_no.value"
            :readonly="isReadOnly"
            :remote="true"
            :withDetails="true"
            :allow-new-record="true"
            :error="
              fields.purchase_document_no.touched
                ? fields.purchase_document_no.error
                : null
            "
            @blur="fields.purchase_document_no.touched = true"
            @select="handleInvoiceSelect"
          />
        </div>

        <div>
          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Date
          </label>
          <input
            v-model="formData.date_time"
            :readonly="isReadOnly"
            type="date"
            class="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700"
          />
        </div>
        <div>
          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Remarks
          </label>
          <input
            v-model="fields.remarks.value"
            type="text"
            :readonly="isReadOnly"
            class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
          />
        </div>
        <div>
          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Attachment
          </label>
          <input
            v-model="fields.attachment.value"
            type="text"
            :readonly="isReadOnly"
            class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
          />
        </div>
        <div>
          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Document Number
          </label>
          <input
            v-model="fields.document_no.value"
            type="text"
            readonly
            class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
          />
        </div>
      </div>

      <div class="mb-6" v-if="!isMobile">
        <TabComponent
          :tabs="[
            { name: 'items', label: 'Items', visible: true },
            { name: 'bills', label: 'Bills', visible: invoiceItems.length > 0 },
            {
              name: 'expenses',
              label: 'Expenses',
              visible: invoiceItems.length > 0,
            },
            {
              name: 'accounting_changes',
              label: 'Changes to Ledgers',
              visible: invoiceItems.length > 0 && !isReadOnly,
            },
            {
              name: 'accounting',
              label: 'Accounting',
              visible: isReadOnly,
            },
          ]"
          v-model="activeTab"
        >
          <template #items>
            <DataTable
              title="Items"
              :fields="itemTableFields"
              :rows="invoiceItems"
              :show-search="false"
              :show-filter="false"
              :show-create="false"
              :show-actions="false"
              :actions="[
                {
                  name: 'select',
                  permission: 'view',
                  icon: 'MousePointer2',
                  variant: 'primary',
                  title: 'Select',
                  visible: () => true,
                  ignorePermission: true,
                  hideMobile: true,
                },
              ]"
              :showEdit="false"
              :showDelete="false"
              :showAdd="false"
              :showSelect="true"
            />
          </template>
          <template #bills>
            <DataTable
              title="Bills"
              :fields="[
                {
                  name: 'document_no',
                  caption: 'Ref. No',
                  sortable: false,
                  fieldType: 'text',
                },
                {
                  name: 'vendor',
                  caption: 'Vendor',
                  sortable: false,
                  fieldType: 'text',
                },
                {
                  name: 'amount',
                  caption: 'Amount',
                  sortable: false,
                  fieldType: 'number',
                  width: '150px',
                },
                {
                  name: 'absorbment',
                  caption: 'Absorbment',
                  sortable: false,
                  fieldType: 'text',
                  width: '200px',
                },
                {
                  name: 'remarks',
                  caption: 'Remarks',
                  sortable: false,
                  fieldType: 'text',
                },
              ]"
              :rows="bills"
              :show-search="false"
              :show-filter="false"
              :show-create="false"
              :show-actions="false"
              :actions="[
                {
                  name: 'select',
                  permission: 'view',
                  icon: 'MousePointer2',
                  variant: 'primary',
                  title: 'Select',
                  visible: () => true,
                  ignorePermission: true,
                  hideMobile: true,
                },
              ]"
              :showEdit="false"
              :showDelete="false"
              :showAdd="false"
              :showSelect="true"
              :show-child-row="true"
              width="400px"
              :child-row-fields="[
                {
                  name: 'code',
                  caption: 'Code',
                  sortable: false,
                  fieldType: 'text',
                },
                {
                  name: 'name',
                  caption: 'Item Name',
                  sortable: false,
                  fieldType: 'text',
                },
                {
                  name: 'amount',
                  caption: 'Amount',
                  sortable: false,
                  fieldType: 'number',
                },
                {
                  name: 'ratio',
                  caption: 'Ratio',
                  sortable: false,
                  fieldType: 'number',
                },
              ]"
              child-row-title="Items"
              child-row-data-field="items"
              child-row-table-width="300px"
            >
              <template #list-table-header-actions>
                <Buttons
                  caption="Add New Bill"
                  icon="Plus"
                  variant="primary"
                  class="ml-2"
                  @click="handleAddNewBill"
                />
              </template>
            </DataTable>
          </template>
          <template #expenses>
            <DataTable
              title="Expenses"
              :fields="[
                {
                  name: 'document_no',
                  caption: 'Expense/Account',
                  sortable: false,
                  fieldType: 'text',
                },
                {
                  name: 'amount',
                  caption: 'Amount',
                  sortable: false,
                  fieldType: 'number',
                  width: '150px',
                },
                {
                  name: 'absorbment',
                  caption: 'Absorbment',
                  sortable: false,
                  fieldType: 'text',
                  width: '200px',
                },
                {
                  name: 'remarks',
                  caption: 'Remarks',
                  sortable: false,
                  fieldType: 'text',
                },
              ]"
              :rows="expenses"
              :show-search="false"
              :show-filter="false"
              :show-create="false"
              :show-actions="false"
              :showEdit="false"
              :showDelete="false"
              :showAdd="false"
              :showSelect="true"
              :show-child-row="true"
              width="400px"
              :child-row-fields="[
                {
                  name: 'code',
                  caption: 'Code',
                  sortable: false,
                  fieldType: 'text',
                },
                {
                  name: 'name',
                  caption: 'Item Name',
                  sortable: false,
                  fieldType: 'text',
                },
                {
                  name: 'amount',
                  caption: 'Amount',
                  sortable: false,
                  fieldType: 'number',
                },
                {
                  name: 'ratio',
                  caption: 'Ratio',
                  sortable: false,
                  fieldType: 'number',
                },
              ]"
              child-row-title="Items"
              child-row-data-field="items"
              child-row-table-width="300px"
            >
              <template #list-table-header-actions>
                <Buttons
                  caption="Add New Expense"
                  icon="Plus"
                  variant="primary"
                  class="ml-2"
                  @click="handleAddNewExpense"
                />
              </template>
            </DataTable>
          </template>
          <template #accounting_changes>
            <DataTable
              title="Ledgers"
              :fields="[
                {
                  name: 'chart_of_account',
                  caption: 'Account',
                  sortable: false,
                  fieldType: 'text',
                },
                {
                  name: 'debit',
                  caption: 'Debit',
                  sortable: false,
                  fieldType: 'number',
                  width: '150px',
                },
                {
                  name: 'credit',
                  caption: 'Credit',
                  sortable: false,
                  fieldType: 'number',
                  width: '150px',
                },
                {
                  name: 'reference',
                  caption: 'Reference',
                  sortable: false,
                  fieldType: 'text',
                  width: '200px',
                },
                {
                  name: 'remarks',
                  caption: 'Remarks',
                  sortable: false,
                  fieldType: 'text',
                },
              ]"
              :rows="ledgers"
              :show-search="false"
              :show-filter="false"
              :show-create="false"
              :show-actions="false"
              :showEdit="false"
              :showDelete="false"
              :showAdd="false"
              :showSelect="true"
            >
            </DataTable>
          </template>
          <template #accounting>
            <AccountingLog :data="formData.accounting" v-if="isReadOnly" />
          </template>
        </TabComponent>
      </div>
      <ListSelectModalInput
        v-if="showBillsForm"
        end-point="procurement/purchases/purchase-invoices"
        :label="'Select Bills'"
        :fields="[
          {
            name: 'document_no',
            caption: 'Ref. No',
            sortable: false,
            fieldType: 'text',
          },
          {
            name: 'date_time',
            caption: 'Date',
            sortable: false,
            fieldType: 'date',
          },
          {
            name: 'vendor',
            caption: 'Vendor',
            sortable: false,
            fieldType: 'text',
          },
          {
            name: 'value_before_tax',
            caption: 'Amount',
            sortable: false,
            fieldType: 'number',
          },
          {
            name: 'remarks',
            caption: 'Remarks',
            sortable: false,
            fieldType: 'text',
          },
        ]"
        @close="showBillsForm = false"
        @select="handleAddBills"
      />
      <ListSelectModalInput
        v-if="showExpenseForm"
        custom-url="procurement/purchases/costing-sheets/get-expenses"
        :label="'Select Expenses'"
        :fields="[
          {
            name: 'chart_of_account',
            caption: 'Account',
            sortable: false,
            fieldType: 'text',
          },
          {
            name: 'date',
            caption: 'Date',
            sortable: false,
            fieldType: 'date',
          },
          {
            name: 'currency',
            caption: 'Currency',
            sortable: false,
            fieldType: 'text',
          },
          {
            name: 'amount',
            caption: 'Amount',
            sortable: false,
            fieldType: 'number',
            width: '150px',
          },
          {
            name: 'remarks',
            caption: 'Remarks',
            sortable: false,
            fieldType: 'text',
          },
          {
            name: 'reference',
            caption: 'Reference',
            sortable: false,
            fieldType: 'text',
          },
        ]"
        @close="showExpenseForm = false"
        @select="handleAddExpense"
      />

      <ModalFormTemplate
        mode="create"
        width="45rem"
        v-if="showBillAbsorbForm && selectedBill"
        end-point=""
        title="Absorb Bills"
        @save="handleUpdateBills"
        @close="showBillAbsorbForm = false"
        :ignore-permission="true"
      >
        <template #form>
          <div class="grid md:grid-cols-3 gap-6 mb-8">
            <div>
              <TextInput
                v-model="selectedBill.document_no"
                label="Bill Number"
                :readonly="true"
              />
            </div>
            <div>
              <NumberInput
                :show-controls="false"
                v-model="selectedBill.amount"
                label="Amount"
                :readonly="false"
              />
            </div>
            <div class="">
              <SearchSelect
                v-model="selectedBill.absorbment"
                label="Absorbment Method"
                @select="handleAbsorbmentChanged"
                :options="[
                  { id: 'quantity', name: 'Quantity' },
                  { id: 'amount', name: 'Amount' },
                  { id: 'equally', name: 'Equally' },
                ]"
              />
            </div>
          </div>
          <div>
            <DataTable
              title="Items"
              :fields="[
                {
                  name: 'code',
                  caption: 'Code',
                  sortable: true,
                  fieldType: 'text',
                },
                {
                  name: 'name',
                  caption: 'Item Name',
                  sortable: true,
                  fieldType: 'text',
                },
                {
                  name: 'amount',
                  caption: 'Amount',
                  sortable: true,
                  fieldType: 'number',
                  width: '150px',
                },
              ]"
              :rows="selectedBill.items"
              :show-search="false"
              :show-filter="false"
              :show-create="false"
              :show-actions="false"
              :showEdit="false"
              :showDelete="false"
              :showAdd="false"
              :showSelect="true"
            />
          </div>
        </template>
      </ModalFormTemplate>
      <ModalFormTemplate
        mode="create"
        width="45rem"
        v-if="showExpenseAbsorbForm && selectedBill"
        end-point=""
        title="Absorb Expenses"
        @save="handleUpdateExpenses"
        @close="showExpenseAbsorbForm = false"
        :ignore-permission="true"
      >
        <template #form>
          <div class="grid md:grid-cols-3 gap-6 mb-8">
            <div>
              <TextInput
                v-model="selectedBill.document_no"
                label="Account Name"
                :readonly="true"
              />
            </div>
            <div>
              <NumberInput
                :show-controls="false"
                v-model="selectedBill.amount"
                label="Amount"
                :readonly="false"
              />
            </div>
            <div class="">
              <SearchSelect
                v-model="selectedBill.absorbment"
                label="Absorbment Method"
                @select="handleAbsorbmentChanged"
                :options="[
                  { id: 'quantity', name: 'Quantity' },
                  { id: 'amount', name: 'Amount' },
                  { id: 'equally', name: 'Equally' },
                ]"
              />
            </div>
          </div>
          <div>
            <DataTable
              title="Items"
              :fields="[
                {
                  name: 'code',
                  caption: 'Code',
                  sortable: true,
                  fieldType: 'text',
                },
                {
                  name: 'name',
                  caption: 'Item Name',
                  sortable: true,
                  fieldType: 'text',
                },
                {
                  name: 'amount',
                  caption: 'Amount',
                  sortable: true,
                  fieldType: 'number',
                  width: '150px',
                },
              ]"
              :rows="selectedBill.items"
              :show-search="false"
              :show-filter="false"
              :show-create="false"
              :show-actions="false"
              :showEdit="false"
              :showDelete="false"
              :showAdd="false"
              :showSelect="true"
            />
          </div>
        </template>
      </ModalFormTemplate>
    </template>
    <!-- Footer -->
    <template #footer>
      <DocumentFooter
        :data="formData"
        :readonly="isReadOnly"
        :end-point="endPoint"
        :permissions="permissions"
        @save="handleSubmit"
        :title="title"
        convert-module="Debit Note"
        @close="$emit('close')"
        @new="clearForm"
        :show-share-icons="false"
      />
    </template>
  </ModalFormComponent>
</template>
